/**
 * ProjectFinanceEntries — Manuelle Einnahmen/Ausgaben Tabelle für ein Projekt.
 *
 * Eingebettet ins Projekt-Controlling. Liest Daten aus:
 *   GET    /api/projects/{pid}/finance-entries
 *   POST   /api/projects/{pid}/finance-entries
 *   PATCH  /api/projects/{pid}/finance-entries/{id}
 *   DELETE /api/projects/{pid}/finance-entries/{id}
 *   POST   /api/projects/{pid}/finance-entries/{id}/upload-receipt
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { authFetch } from "../App";
import { toast } from "sonner";
import {
  Plus, X, Trash2, Edit3, Receipt, ArrowUpRight, ArrowDownRight, Wallet,
  CheckCircle2, Clock, Filter, Upload, FileText, Loader2, Copy,
  TrendingUp, TrendingDown, FileWarning, Receipt as ReceiptIcon,
} from "lucide-react";

const API = "/api";

const EXPENSE_CATEGORIES = [
  "Material", "Unterkunft", "Fahrtkosten", "Werkzeug", "Gerätemiete",
  "Nachunternehmer", "Fremdleistung", "Parken", "Verpflegung", "Sonstiges",
];
const INCOME_CATEGORIES = [
  "Abschlagszahlung", "Schlussrechnung", "Nachtrag", "Zahlungseingang", "Gutschrift", "Sonstiges",
];

// Status je Typ — fachlich getrennt:
// Einnahmen (Rechnungsstatus): Entwurf, Gestellt, Teilweise bezahlt, Bezahlt, Storniert
// Ausgaben (Zahlungsstatus):   Offen,   Teilweise bezahlt, Bezahlt, Storniert
const INCOME_STATUS_OPTIONS = [
  { value: "entwurf", label: "Entwurf", color: "slate" },
  { value: "gestellt", label: "Gestellt", color: "amber" },
  { value: "teilweise_bezahlt", label: "Teilweise bezahlt", color: "blue" },
  { value: "bezahlt", label: "Bezahlt", color: "emerald" },
  { value: "storniert", label: "Storniert", color: "gray" },
];
const EXPENSE_STATUS_OPTIONS = [
  { value: "offen", label: "Offen", color: "amber" },
  { value: "teilweise_bezahlt", label: "Teilweise bezahlt", color: "blue" },
  { value: "bezahlt", label: "Bezahlt", color: "emerald" },
  { value: "storniert", label: "Storniert", color: "gray" },
];
// Legacy-Mapping für Bestandsdaten (UI-Anzeige)
const LEGACY_STATUS_MAP = {
  income: { open: "gestellt", paid: "bezahlt", partial: "teilweise_bezahlt", cancelled: "storniert" },
  expense: { open: "offen", paid: "bezahlt", partial: "teilweise_bezahlt", cancelled: "storniert" },
};
const normalizeStatus = (type, status) => {
  if (!status) return type === "income" ? "gestellt" : "offen";
  return (LEGACY_STATUS_MAP[type] && LEGACY_STATUS_MAP[type][status]) || status;
};
const statusOptionsFor = (type) =>
  type === "income" ? INCOME_STATUS_OPTIONS : EXPENSE_STATUS_OPTIONS;
const statusMetaFor = (type, status) => {
  const norm = normalizeStatus(type, status);
  return statusOptionsFor(type).find((s) => s.value === norm) || statusOptionsFor(type)[0];
};
// Tailwind-Sicherheit: bekannte Tone-Klassen vorab listen, damit JIT sie behält
// bg-slate-500/15 text-slate-300 bg-amber-500/15 text-amber-300 bg-blue-500/15 text-blue-300
// bg-emerald-500/15 text-emerald-300 bg-gray-500/15 text-gray-300

const EUR = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

const EMPTY_ENTRY = {
  date: new Date().toISOString().slice(0, 10),
  type: "expense",
  category: "Material",
  description: "",
  amount_net: "",
  vat_rate: 19,
  payment_status: "offen",
  paid_amount: "",
  payment_date: "",
  due_date: "",
  notes: "",
};

export default function ProjectFinanceEntries({ projectId, canWrite, highlightEntryId = null }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: "all", status: "all", category: "all" });
  const [editing, setEditing] = useState(null); // null | "new" | entry.id
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [eRes, sRes] = await Promise.all([
        authFetch(`${API}/projects/${projectId}/finance-entries`),
        authFetch(`${API}/projects/${projectId}/finance-summary`),
      ]);
      if (eRes.ok) setEntries((await eRes.json()).entries || []);
      if (sRes.ok) setSummary(await sRes.json());
    } catch (e) { /* keep state */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return entries.filter(e => {
      if (filter.type !== "all" && e.type !== filter.type) return false;
      if (filter.status !== "all") {
        const norm = normalizeStatus(e.type, e.payment_status);
        if (norm !== filter.status) return false;
      }
      if (filter.category !== "all" && e.category !== filter.category) return false;
      return true;
    });
  }, [entries, filter]);

  const startNew = () => {
    setForm({ ...EMPTY_ENTRY });
    setEditing("new");
  };

  const startEdit = (entry) => {
    setForm({
      date: entry.date,
      type: entry.type,
      category: entry.category,
      description: entry.description || "",
      amount_net: String(entry.amount_net),
      vat_rate: entry.vat_rate,
      payment_status: normalizeStatus(entry.type, entry.payment_status),
      paid_amount: entry.paid_amount != null ? String(entry.paid_amount).replace(".", ",") : "",
      payment_date: entry.payment_date || "",
      due_date: entry.due_date || "",
      notes: entry.notes || "",
    });
    setEditing(entry.id);
  };

  const cancel = () => { setEditing(null); setForm(EMPTY_ENTRY); };

  const save = async () => {
    const net = parseFloat(form.amount_net) || 0;
    if (!form.date) { toast.error("Datum fehlt"); return; }
    if (!form.category) { toast.error("Kategorie fehlt"); return; }
    if (net <= 0) { toast.error("Betrag muss > 0 sein"); return; }
    setSaving(true);
    try {
      const paidRaw = String(form.paid_amount || "").trim().replace(/\./g, "").replace(",", ".");
      const paidParsed = paidRaw === "" ? null : Number(paidRaw);
      const payload = {
        date: form.date,
        type: form.type,
        category: form.category,
        description: form.description,
        amount_net: net,
        vat_rate: parseFloat(form.vat_rate) || 0,
        payment_status: form.payment_status,
        paid_amount: Number.isFinite(paidParsed) ? paidParsed : undefined,
        payment_date: form.payment_date || null,
        due_date: form.due_date || null,
        notes: form.notes,
      };
      const isNew = editing === "new";
      const url = isNew
        ? `${API}/projects/${projectId}/finance-entries`
        : `${API}/projects/${projectId}/finance-entries/${editing}`;
      const r = await authFetch(url, {
        method: isNew ? "POST" : "PATCH",
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Speichern fehlgeschlagen");
        setSaving(false);
        return;
      }
      toast.success(isNew ? "Eintrag erstellt" : "Eintrag aktualisiert");
      cancel();
      await load();
    } catch { toast.error("Netzwerkfehler"); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    const r = await authFetch(`${API}/projects/${projectId}/finance-entries/${id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Gelöscht"); load(); }
    else toast.error("Löschen fehlgeschlagen");
  };

  // Inline-Patch eines einzelnen Felds (oder mehrerer Felder gleichzeitig)
  // Liefert true bei Erfolg, false bei Fehler — der Caller stellt im Fehlerfall
  // den alten Wert wieder her.
  const patchInline = useCallback(async (entryId, patch) => {
    try {
      const r = await authFetch(`${API}/projects/${projectId}/finance-entries/${entryId}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Speichern fehlgeschlagen");
        return false;
      }
      // Optimistic UI: refresh nur die Summary, Tabelle wird durch load() neu geladen
      // (kompletter Reload bleibt einfach + sicher)
      await load();
      return true;
    } catch {
      toast.error("Netzwerkfehler");
      return false;
    }
  }, [projectId, load]);

  // Eintrag duplizieren — selbe Grunddaten, Beleg NICHT mitkopieren, Datum = heute
  const duplicate = useCallback(async (entry) => {
    const newStatus = entry.type === "income" ? "gestellt" : "offen";
    const payload = {
      date: new Date().toISOString().slice(0, 10),
      type: entry.type,
      category: entry.category,
      description: entry.description ? `${entry.description} (Kopie)` : "",
      amount_net: entry.amount_net,
      vat_rate: entry.vat_rate,
      amount_gross: entry.amount_gross,
      payment_status: newStatus,
      paid_amount: 0,
      notes: entry.notes || "",
    };
    try {
      const r = await authFetch(`${API}/projects/${projectId}/finance-entries`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        toast.error("Duplizieren fehlgeschlagen");
        return;
      }
      toast.success("Eintrag dupliziert");
      await load();
    } catch {
      toast.error("Netzwerkfehler");
    }
  }, [projectId, load]);

  const uploadReceipt = async (entryId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Datei > 10 MB"); return; }
    setUploadingFor(entryId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authFetch(`${API}/projects/${projectId}/finance-entries/${entryId}/upload-receipt`, {
        method: "POST", body: fd,
      });
      if (r.ok) { toast.success("Beleg hochgeladen"); load(); }
      else toast.error("Upload fehlgeschlagen");
    } catch { toast.error("Netzwerkfehler"); }
    setUploadingFor(null);
  };

  const gross = useMemo(() => {
    const n = parseFloat(form.amount_net) || 0;
    const v = parseFloat(form.vat_rate) || 0;
    return n * (1 + v / 100);
  }, [form.amount_net, form.vat_rate]);

  return (
    <div className="space-y-4" data-testid="finance-entries-section">
      {/* Summary-Karten — fachlich getrennt: Forderungen, Verbindlichkeiten, Ist-Cashflow, Prognose */}
      {summary && <FinanceSummaryCards summary={summary} />}

      {/* Filter + Add */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-xs">
          <Filter className="w-3.5 h-3.5 opacity-50" />
          <select value={filter.type} onChange={e => setFilter(p => ({ ...p, type: e.target.value }))}
            className="px-2 py-1 rounded border bg-white/5 text-xs" data-testid="finance-filter-type">
            <option value="all">Alle Typen</option>
            <option value="income">Einnahmen</option>
            <option value="expense">Ausgaben</option>
          </select>
          <select value={filter.status} onChange={e => setFilter(p => ({ ...p, status: e.target.value }))}
            className="px-2 py-1 rounded border bg-white/5 text-xs" data-testid="finance-filter-status">
            <option value="all">Alle Status</option>
            <optgroup label="Einnahmen">
              {INCOME_STATUS_OPTIONS.map(s => <option key={"i-" + s.value} value={s.value}>{s.label}</option>)}
            </optgroup>
            <optgroup label="Ausgaben">
              {EXPENSE_STATUS_OPTIONS.map(s => <option key={"e-" + s.value} value={s.value}>{s.label}</option>)}
            </optgroup>
          </select>
          <select value={filter.category} onChange={e => setFilter(p => ({ ...p, category: e.target.value }))}
            className="px-2 py-1 rounded border bg-white/5 text-xs" data-testid="finance-filter-category">
            <option value="all">Alle Kategorien</option>
            <optgroup label="Ausgaben">
              {EXPENSE_CATEGORIES.map(c => <option key={"e-" + c} value={c}>{c}</option>)}
            </optgroup>
            <optgroup label="Einnahmen">
              {INCOME_CATEGORIES.map(c => <option key={"i-" + c} value={c}>{c}</option>)}
            </optgroup>
          </select>
        </div>
        <div className="flex-1" />
        {canWrite && editing === null && (
          <button onClick={startNew}
            className="px-3 h-9 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold flex items-center gap-1.5"
            data-testid="finance-add-btn">
            <Plus className="w-3.5 h-3.5" />
            Einnahme / Ausgabe
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {editing !== null && (
        <FinanceForm
          form={form}
          setForm={setForm}
          gross={gross}
          isNew={editing === "new"}
          onCancel={cancel}
          onSave={save}
          saving={saving}
        />
      )}

      {/* Tabelle */}
      {loading ? (
        <div className="text-xs text-gray-500 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /> Laden...</div>
      ) : filtered.length === 0 ? (
        <div className="text-xs text-gray-500 py-12 text-center border border-dashed border-white/10 rounded-lg">
          Keine Buchungen vorhanden.
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/10 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.04] border-b border-white/10">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">Datum</th>
                <th className="px-2 py-2 font-semibold">Typ</th>
                <th className="px-2 py-2 font-semibold">Kategorie</th>
                <th className="px-3 py-2 font-semibold">Beschreibung</th>
                <th className="px-3 py-2 font-semibold text-right">Netto</th>
                <th className="px-2 py-2 font-semibold text-right">MwSt</th>
                <th className="px-3 py-2 font-semibold text-right">Brutto</th>
                <th className="px-2 py-2 font-semibold">Status</th>
                <th className="px-2 py-2 font-semibold">Beleg</th>
                {canWrite && <th className="px-2 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <FinanceRow
                  key={e.id}
                  entry={e}
                  canWrite={canWrite}
                  highlight={highlightEntryId === e.id}
                  onEdit={() => startEdit(e)}
                  onDelete={() => remove(e.id)}
                  onDuplicate={() => duplicate(e)}
                  onPatch={(patch) => patchInline(e.id, patch)}
                  onUploadReceipt={(file) => uploadReceipt(e.id, file)}
                  uploading={uploadingFor === e.id}
                  projectId={projectId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, sub, tone, testid }) {
  const tones = {
    emerald: "from-emerald-500/20 to-emerald-700/5 border-emerald-500/30 text-emerald-200",
    rose: "from-rose-500/20 to-rose-700/5 border-rose-500/30 text-rose-200",
    amber: "from-amber-500/20 to-amber-700/5 border-amber-500/30 text-amber-200",
    blue: "from-blue-500/20 to-blue-700/5 border-blue-500/30 text-blue-200",
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${tones[tone]} p-3`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4 opacity-70" />
        <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</p>
      </div>
      <p className="text-xl font-extrabold tabular-nums">{value}</p>
      {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

/**
 * Vier Hauptkarten: Forderungen / Verbindlichkeiten / Ist-Cashflow / Prognose Ergebnis
 * Fachlich getrennt zwischen "Geld bereits geflossen" und "wirtschaftlich relevant".
 */
function FinanceSummaryCards({ summary }) {
  // Fallbacks für ältere Backend-Versionen, die noch keine neuen Felder liefern
  const invoiced = summary.income_invoiced ?? summary.income_gross ?? 0;
  const incomePaid = summary.income_paid ?? 0;
  const incomeOpen = summary.income_open ?? Math.max(invoiced - incomePaid, 0);
  const expExpected = summary.expense_expected ?? summary.expense_gross ?? 0;
  const expPaid = summary.expense_paid ?? 0;
  const expOpen = summary.expense_open ?? Math.max(expExpected - expPaid, 0);
  const cashflow = summary.cashflow_ist ?? (incomePaid - expPaid);
  const forecast = summary.forecast_result ?? (invoiced - expExpected);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="finance-summary-cards">
      <SummaryCard
        icon={ReceiptIcon}
        title="Forderungen"
        tone="emerald"
        rows={[
          { label: "Gestellt", value: invoiced, color: "text-emerald-200" },
          { label: "Bezahlt", value: incomePaid, color: "text-emerald-300" },
          { label: "Offen", value: incomeOpen, color: "text-amber-300" },
        ]}
        testid="finance-card-forderungen"
      />
      <SummaryCard
        icon={FileWarning}
        title="Verbindlichkeiten"
        tone="rose"
        rows={[
          { label: "Erwartet", value: expExpected, color: "text-rose-200" },
          { label: "Bezahlt", value: expPaid, color: "text-rose-300" },
          { label: "Offen", value: expOpen, color: "text-amber-300" },
        ]}
        testid="finance-card-verbindlichkeiten"
      />
      <BigNumberCard
        icon={Wallet}
        title="Ist-Cashflow"
        value={cashflow}
        hint="Bezahlte Einnahmen − bezahlte Ausgaben"
        tone={cashflow >= 0 ? "blue" : "rose"}
        testid="finance-card-cashflow"
      />
      <BigNumberCard
        icon={cashflow > forecast ? TrendingDown : TrendingUp}
        title="Prognose Ergebnis"
        value={forecast}
        hint="Gestellte Einnahmen − erwartete Ausgaben"
        tone={forecast >= 0 ? "emerald" : "rose"}
        testid="finance-card-prognose"
      />
    </div>
  );
}

function SummaryCard({ icon: Icon, title, rows, tone, testid }) {
  const tones = {
    emerald: "border-emerald-500/30 bg-emerald-500/[0.06]",
    rose: "border-rose-500/30 bg-rose-500/[0.06]",
    blue: "border-blue-500/30 bg-blue-500/[0.06]",
    amber: "border-amber-500/30 bg-amber-500/[0.06]",
  };
  return (
    <div
      className={`rounded-xl border ${tones[tone] || tones.blue} p-4 backdrop-blur-sm`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 opacity-70" />
        <p className="text-[11px] uppercase tracking-widest font-bold opacity-80 text-white/90">{title}</p>
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between text-xs">
            <span className="opacity-60">{r.label}</span>
            <span className={`tabular-nums font-bold text-sm ${r.color}`}>{EUR.format(r.value || 0)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BigNumberCard({ icon: Icon, title, value, hint, tone, testid }) {
  const tones = {
    emerald: "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-200",
    rose: "border-rose-500/30 bg-rose-500/[0.06] text-rose-200",
    blue: "border-blue-500/30 bg-blue-500/[0.06] text-blue-200",
    amber: "border-amber-500/30 bg-amber-500/[0.06] text-amber-200",
  };
  return (
    <div
      className={`rounded-xl border ${tones[tone] || tones.blue} p-4 backdrop-blur-sm flex flex-col justify-between`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 opacity-70" />
        <p className="text-[11px] uppercase tracking-widest font-bold opacity-80">{title}</p>
      </div>
      <p className="text-2xl font-extrabold tabular-nums" data-testid={`${testid}-value`}>
        {EUR.format(value || 0)}
      </p>
      {hint && <p className="text-[10px] opacity-50 mt-1">{hint}</p>}
    </div>
  );
}

function FinanceForm({ form, setForm, gross, isNew, onCancel, onSave, saving }) {
  const cats = form.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  const statusOpts = statusOptionsFor(form.type);
  const onChange = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isPartial = form.payment_status === "teilweise_bezahlt";
  return (
    <div className="rounded-lg border border-blue-500/30 bg-blue-500/[0.05] p-3 space-y-3" data-testid="finance-form">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <Lbl>
          Datum
          <input type="date" value={form.date} onChange={e => onChange("date", e.target.value)}
            className="w-full px-2 py-1.5 rounded border bg-transparent text-sm" data-testid="finance-form-date" />
        </Lbl>
        <Lbl>
          Typ
          <select value={form.type}
            onChange={e => {
              const newType = e.target.value;
              const newCats = newType === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
              const newStatus = newType === "income" ? "gestellt" : "offen";
              setForm(p => ({
                ...p,
                type: newType,
                category: newCats.includes(p.category) ? p.category : newCats[0],
                payment_status: newStatus,
              }));
            }}
            className="w-full px-2 py-1.5 rounded border bg-transparent text-sm" data-testid="finance-form-type">
            <option value="expense">Ausgabe</option>
            <option value="income">Einnahme</option>
          </select>
        </Lbl>
        <Lbl>
          Kategorie
          <select value={form.category} onChange={e => onChange("category", e.target.value)}
            className="w-full px-2 py-1.5 rounded border bg-transparent text-sm" data-testid="finance-form-category">
            {cats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Lbl>
        <Lbl>
          Status
          <select value={form.payment_status} onChange={e => onChange("payment_status", e.target.value)}
            className="w-full px-2 py-1.5 rounded border bg-transparent text-sm" data-testid="finance-form-status">
            {statusOpts.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Lbl>
      </div>

      <Lbl>
        Beschreibung
        <input type="text" value={form.description} onChange={e => onChange("description", e.target.value)}
          placeholder="z.B. Kupferkabel 100m / Abschlagsrechnung 1"
          className="w-full px-2 py-1.5 rounded border bg-transparent text-sm" data-testid="finance-form-description" />
      </Lbl>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Lbl>
          Betrag netto (€)
          <input type="number" step="0.01" value={form.amount_net}
            onChange={e => onChange("amount_net", e.target.value)}
            className="w-full px-2 py-1.5 rounded border bg-transparent text-sm tabular-nums" data-testid="finance-form-net" />
        </Lbl>
        <Lbl>
          MwSt %
          <input type="number" step="0.1" value={form.vat_rate}
            onChange={e => onChange("vat_rate", e.target.value)}
            className="w-full px-2 py-1.5 rounded border bg-transparent text-sm tabular-nums" data-testid="finance-form-vat" />
        </Lbl>
        <Lbl>
          Brutto (€)
          <input type="text" readOnly value={EUR.format(gross || 0)}
            className="w-full px-2 py-1.5 rounded border bg-white/[0.04] text-sm tabular-nums font-bold" data-testid="finance-form-gross-readonly" />
        </Lbl>
      </div>

      {/* Teilzahlung + Termine */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {isPartial && (
          <Lbl>
            Bezahlter Betrag (€)
            <input type="text" inputMode="decimal" value={form.paid_amount}
              onChange={e => onChange("paid_amount", e.target.value)}
              placeholder="z.B. 1.500,00"
              className="w-full px-2 py-1.5 rounded border bg-transparent text-sm tabular-nums"
              data-testid="finance-form-paid-amount" />
          </Lbl>
        )}
        <Lbl>
          Zahlungsdatum (optional)
          <input type="date" value={form.payment_date}
            onChange={e => onChange("payment_date", e.target.value)}
            className="w-full px-2 py-1.5 rounded border bg-transparent text-sm"
            data-testid="finance-form-payment-date" />
        </Lbl>
        <Lbl>
          Fälligkeitsdatum (optional)
          <input type="date" value={form.due_date}
            onChange={e => onChange("due_date", e.target.value)}
            className="w-full px-2 py-1.5 rounded border bg-transparent text-sm"
            data-testid="finance-form-due-date" />
        </Lbl>
      </div>

      <Lbl>
        Notiz (optional)
        <textarea value={form.notes} onChange={e => onChange("notes", e.target.value)}
          rows={2}
          className="w-full px-2 py-1.5 rounded border bg-transparent text-sm" data-testid="finance-form-notes" />
      </Lbl>

      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} disabled={saving}
          className="px-3 h-8 rounded border text-xs" data-testid="finance-form-cancel">
          Abbrechen
        </button>
        <button onClick={onSave} disabled={saving}
          className="px-3 h-8 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold disabled:opacity-50"
          data-testid="finance-form-save">
          {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
          {isNew ? "Erstellen" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

function Lbl({ children }) {
  const [labelText, ...rest] = Array.isArray(children) ? children : [children];
  return (
    <label className="text-xs space-y-0.5 block">
      <span className="text-gray-500">{labelText}</span>
      {rest}
    </label>
  );
}

function FinanceRow({ entry, canWrite, highlight = false, onEdit, onDelete, onDuplicate, onPatch, onUploadReceipt, uploading, projectId }) {
  const status = statusMetaFor(entry.type, entry.payment_status);
  const statusOpts = statusOptionsFor(entry.type);
  const normalizedStatus = normalizeStatus(entry.type, entry.payment_status);
  const isIncome = entry.type === "income";
  const rowRef = useRef(null);
  const [pulse, setPulse] = useState(false);
  const [editingField, setEditingField] = useState(null);   // "date" | "type" | "category" | "description" | "amount_net" | "vat_rate" | "amount_gross" | "status" | null
  const [savingField, setSavingField] = useState(null);     // welches Feld wird gerade gespeichert
  const [savedFlash, setSavedFlash] = useState(null);       // welches Feld zeigt grünes "gespeichert"-Flash

  useEffect(() => {
    if (!highlight) return;
    const timer = setTimeout(() => {
      rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setPulse(true);
      setTimeout(() => setPulse(false), 2400);
    }, 200);
    return () => clearTimeout(timer);
  }, [highlight]);

  const beginEdit = (field) => {
    if (!canWrite) return;
    setEditingField(field);
  };
  const finishEdit = () => setEditingField(null);

  // Deutsches Format → number ("1.222,00" → 1222.00); leerer String → null; ungültig → NaN
  const parseEuro = (raw) => {
    if (raw === "" || raw == null) return null;
    const cleaned = String(raw).trim().replace(/\./g, "").replace(",", ".");
    const num = Number(cleaned);
    if (!isFinite(num) || num < 0) return NaN;
    return num;
  };

  // Generischer Save-Helper
  const commit = async (field, patch) => {
    setSavingField(field);
    const ok = await onPatch(patch);
    setSavingField(null);
    if (ok) {
      setSavedFlash(field);
      setTimeout(() => setSavedFlash((f) => (f === field ? null : f)), 1200);
    }
    setEditingField(null);
  };

  const commitDate = async (newDate) => {
    if (!newDate || newDate === entry.date) { finishEdit(); return; }
    await commit("date", { date: newDate });
  };
  const commitType = async (newType) => {
    if (newType === entry.type) { finishEdit(); return; }
    // Kategorie wird ggf. ungültig, wenn der Typ wechselt → erste Kategorie der neuen Liste setzen
    const cats = newType === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
    const newCat = cats.includes(entry.category) ? entry.category : cats[0];
    await commit("type", { type: newType, category: newCat });
  };
  const commitCategory = async (newCat) => {
    if (newCat === entry.category) { finishEdit(); return; }
    await commit("category", { category: newCat });
  };
  const commitDescription = async (newDesc) => {
    if (newDesc === (entry.description || "")) { finishEdit(); return; }
    await commit("description", { description: newDesc });
  };
  const commitAmountNet = async (raw) => {
    const parsed = parseEuro(raw);
    if (Number.isNaN(parsed)) { toast.error("Ungültiger Betrag"); finishEdit(); return; }
    if (parsed === entry.amount_net) { finishEdit(); return; }
    const vat = Number(entry.vat_rate) || 0;
    const gross = Math.round((parsed * (1 + vat / 100)) * 100) / 100;
    await commit("amount_net", { amount_net: parsed, amount_gross: gross });
  };
  const commitVat = async (newVat) => {
    const vat = Number(newVat);
    if (!isFinite(vat) || vat < 0) { toast.error("Ungültiger MwSt-Wert"); finishEdit(); return; }
    if (vat === Number(entry.vat_rate)) { finishEdit(); return; }
    const net = Number(entry.amount_net) || 0;
    const gross = Math.round((net * (1 + vat / 100)) * 100) / 100;
    await commit("vat_rate", { vat_rate: vat, amount_gross: gross });
  };
  const commitAmountGross = async (raw) => {
    const parsed = parseEuro(raw);
    if (Number.isNaN(parsed)) { toast.error("Ungültiger Betrag"); finishEdit(); return; }
    if (parsed === entry.amount_gross) { finishEdit(); return; }
    // Brutto ist führend: Netto aus Brutto + MwSt zurückrechnen
    const vat = Number(entry.vat_rate) || 0;
    const net = vat === 0 ? parsed : Math.round((parsed / (1 + vat / 100)) * 100) / 100;
    await commit("amount_gross", { amount_gross: parsed, amount_net: net });
  };
  const commitStatus = async (newStatus) => {
    const norm = normalizeStatus(entry.type, newStatus);
    if (norm === normalizeStatus(entry.type, entry.payment_status)) { finishEdit(); return; }
    await commit("status", { payment_status: norm });
  };

  // Style-Helper für inline-bearbeitbare Zellen
  const cellClass = (field) =>
    `cursor-pointer hover:bg-white/[0.06] rounded ${savedFlash === field ? "bg-emerald-500/10" : ""} ${savingField === field ? "opacity-60" : ""}`;
  const readOnly = !canWrite;

  return (
    <tr
      ref={rowRef}
      className={`border-t border-white/5 hover:bg-white/[0.04] transition-all duration-500 ${
        pulse ? "bg-blue-500/20 shadow-[0_0_0_2px_rgba(59,130,246,0.6)]" : ""
      }`}
      data-testid={`finance-row-${entry.id}`}
    >
      {/* Datum */}
      <td className="px-3 py-2 tabular-nums whitespace-nowrap">
        {editingField === "date" && !readOnly ? (
          <input
            type="date"
            defaultValue={entry.date}
            autoFocus
            onBlur={(e) => commitDate(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") finishEdit(); if (e.key === "Enter") e.target.blur(); }}
            className="bg-slate-900 text-white text-xs px-1.5 py-0.5 rounded border border-blue-500/40"
            data-testid={`finance-inline-date-${entry.id}`}
          />
        ) : (
          <span
            className={cellClass("date") + " px-1 py-0.5 inline-block"}
            onClick={() => beginEdit("date")}
            data-testid={`finance-cell-date-${entry.id}`}
          >
            {formatDate(entry.date)}
          </span>
        )}
      </td>

      {/* Typ */}
      <td className="px-2 py-2">
        {editingField === "type" && !readOnly ? (
          <select
            defaultValue={entry.type}
            autoFocus
            onBlur={(e) => commitType(e.target.value)}
            onChange={(e) => commitType(e.target.value)}
            className="bg-slate-900 text-white text-xs px-1.5 py-0.5 rounded border border-blue-500/40"
            data-testid={`finance-inline-type-${entry.id}`}
          >
            <option value="expense">Ausgabe</option>
            <option value="income">Einnahme</option>
          </select>
        ) : (
          <span
            onClick={() => beginEdit("type")}
            className={cellClass("type") + " inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded " + (
              isIncome ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"
            )}
            data-testid={`finance-cell-type-${entry.id}`}
          >
            {isIncome ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
            {isIncome ? "Einnahme" : "Ausgabe"}
          </span>
        )}
      </td>

      {/* Kategorie */}
      <td className="px-2 py-2">
        {editingField === "category" && !readOnly ? (
          <select
            defaultValue={entry.category}
            autoFocus
            onBlur={(e) => commitCategory(e.target.value)}
            onChange={(e) => commitCategory(e.target.value)}
            className="bg-slate-900 text-white text-xs px-1.5 py-0.5 rounded border border-blue-500/40 max-w-[150px]"
            data-testid={`finance-inline-category-${entry.id}`}
          >
            {(isIncome ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        ) : (
          <span
            onClick={() => beginEdit("category")}
            className={cellClass("category") + " inline-block px-1 py-0.5"}
            data-testid={`finance-cell-category-${entry.id}`}
          >
            {entry.category}
          </span>
        )}
      </td>

      {/* Beschreibung */}
      <td className="px-3 py-2 max-w-[280px]" title={entry.description}>
        {editingField === "description" && !readOnly ? (
          <input
            type="text"
            defaultValue={entry.description || ""}
            autoFocus
            onBlur={(e) => commitDescription(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") finishEdit(); if (e.key === "Enter") e.target.blur(); }}
            className="bg-slate-900 text-white text-xs px-1.5 py-0.5 rounded border border-blue-500/40 w-full"
            data-testid={`finance-inline-description-${entry.id}`}
          />
        ) : (
          <div
            className={cellClass("description") + " truncate px-1 py-0.5"}
            onClick={() => beginEdit("description")}
            data-testid={`finance-cell-description-${entry.id}`}
          >
            {entry.description || <span className="text-gray-500 italic">—</span>}
          </div>
        )}
        <div className="flex items-center gap-1 mt-0.5">
          {entry.source_type === "accommodation" && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-500/15 text-amber-300"
              title="Automatisch aus Unterkunft erzeugt"
              data-testid={`finance-source-acc-${entry.id}`}
            >
              Quelle: Unterkunft
            </span>
          )}
          {entry.manually_edited && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded bg-blue-500/15 text-blue-300"
              title="Manuell bearbeitet — Auto-Sync schreibt diesen Eintrag nicht mehr"
            >
              Manuell bearbeitet
            </span>
          )}
          {entry.auto_cancel_reason && (
            <span
              className="text-[9px] text-gray-400 italic truncate"
              title={entry.auto_cancel_reason}
            >
              {entry.auto_cancel_reason}
            </span>
          )}
        </div>
      </td>

      {/* Netto */}
      <td className="px-3 py-2 text-right tabular-nums">
        {editingField === "amount_net" && !readOnly ? (
          <input
            type="text"
            inputMode="decimal"
            defaultValue={String(entry.amount_net).replace(".", ",")}
            autoFocus
            onBlur={(e) => commitAmountNet(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") finishEdit(); if (e.key === "Enter") e.target.blur(); }}
            className="bg-slate-900 text-white text-xs px-1.5 py-0.5 rounded border border-blue-500/40 w-24 text-right"
            data-testid={`finance-inline-net-${entry.id}`}
          />
        ) : (
          <span
            onClick={() => beginEdit("amount_net")}
            className={cellClass("amount_net") + " inline-block px-1 py-0.5"}
            data-testid={`finance-cell-net-${entry.id}`}
          >
            {EUR.format(entry.amount_net)}
          </span>
        )}
      </td>

      {/* MwSt */}
      <td className="px-2 py-2 text-right tabular-nums opacity-80">
        {editingField === "vat_rate" && !readOnly ? (
          <select
            defaultValue={String(entry.vat_rate)}
            autoFocus
            onBlur={(e) => commitVat(e.target.value)}
            onChange={(e) => commitVat(e.target.value)}
            className="bg-slate-900 text-white text-xs px-1.5 py-0.5 rounded border border-blue-500/40"
            data-testid={`finance-inline-vat-${entry.id}`}
          >
            <option value="0">0 %</option>
            <option value="7">7 %</option>
            <option value="19">19 %</option>
          </select>
        ) : (
          <span
            onClick={() => beginEdit("vat_rate")}
            className={cellClass("vat_rate") + " inline-block px-1 py-0.5"}
            data-testid={`finance-cell-vat-${entry.id}`}
          >
            {entry.vat_rate}%
          </span>
        )}
      </td>

      {/* Brutto */}
      <td className="px-3 py-2 text-right tabular-nums font-bold">
        {editingField === "amount_gross" && !readOnly ? (
          <input
            type="text"
            inputMode="decimal"
            defaultValue={String(entry.amount_gross).replace(".", ",")}
            autoFocus
            onBlur={(e) => commitAmountGross(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") finishEdit(); if (e.key === "Enter") e.target.blur(); }}
            className="bg-slate-900 text-white text-xs px-1.5 py-0.5 rounded border border-blue-500/40 w-24 text-right"
            data-testid={`finance-inline-gross-${entry.id}`}
          />
        ) : (
          <span
            onClick={() => beginEdit("amount_gross")}
            className={cellClass("amount_gross") + " inline-block px-1 py-0.5"}
            data-testid={`finance-cell-gross-${entry.id}`}
          >
            {EUR.format(entry.amount_gross)}
          </span>
        )}
      </td>

      {/* Status */}
      <td className="px-2 py-2">
        {editingField === "status" && !readOnly ? (
          <select
            defaultValue={normalizedStatus}
            autoFocus
            onBlur={(e) => commitStatus(e.target.value)}
            onChange={(e) => commitStatus(e.target.value)}
            className="bg-slate-900 text-white text-xs px-1.5 py-0.5 rounded border border-blue-500/40"
            data-testid={`finance-inline-status-${entry.id}`}
          >
            {statusOpts.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        ) : (
          <span
            onClick={() => beginEdit("status")}
            className={`${cellClass("status")} text-[10px] font-bold px-1.5 py-0.5 rounded bg-${status.color}-500/15 text-${status.color}-300`}
            data-testid={`finance-cell-status-${entry.id}`}
          >
            {status.label}
          </span>
        )}
      </td>

      {/* Beleg */}
      <td className="px-2 py-2">
        {(entry.document_file_id || entry.document_file_path) ? (
          <a href={`${API}/projects/${projectId}/finance-entries/${entry.id}/receipt`} target="_blank" rel="noreferrer"
            className="text-blue-400 hover:text-blue-300" title={entry.document_filename || "Beleg"}
            data-testid={`finance-receipt-view-${entry.id}`}>
            <FileText className="w-3.5 h-3.5" />
          </a>
        ) : canWrite ? (
          <label className="text-gray-500 hover:text-blue-400 cursor-pointer inline-flex items-center" title="Beleg hochladen">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={e => { if (e.target.files?.[0]) onUploadReceipt(e.target.files[0]); }}
              data-testid={`finance-receipt-upload-${entry.id}`} />
          </label>
        ) : "—"}
      </td>

      {/* Aktionen */}
      {canWrite && (
        <td className="px-2 py-2 text-right whitespace-nowrap">
          <button onClick={onDuplicate} className="p-1 hover:bg-white/10 rounded text-blue-400" title="Duplizieren" data-testid={`finance-duplicate-${entry.id}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button onClick={onEdit} className="p-1 hover:bg-white/10 rounded" title="Details bearbeiten (alle Felder + Notiz + Beleg)" data-testid={`finance-edit-${entry.id}`}>
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="p-1 hover:bg-red-500/20 rounded text-red-400" title="Löschen" data-testid={`finance-delete-${entry.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </td>
      )}
    </tr>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  } catch { return iso; }
}
